import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowRight, Flame, Timer, Trophy, Target } from "lucide-react";
import { api } from "../api";
import { useMember } from "../MemberContext";
import { ExerciseCard } from "../ExerciseCard";
import { H1, Overline, PageLoader } from "../ui";
import { Achievements, MotivationCard, TodayCard } from "../DashboardCards";

const Stat = ({ icon: Icon, label, value, sub, testId }) => (
  <div className="bg-[#121212] border border-white/10 p-6" data-testid={testId}>
    <Icon className="w-5 h-5 text-[#c1ff72] mb-6" />
    <p className="font-['Bebas_Neue'] text-5xl leading-none text-white">{value}</p>
    <p className="text-xs uppercase tracking-[0.2em] text-[#71717A] mt-2">{label}</p>
    {sub && <p className="text-xs text-[#A1A1AA] mt-1">{sub}</p>}
  </div>
);

function WeekChart({ weeks }) {
  const data = weeks.map((w) => ({ name: w.week_start.slice(8, 10) + "." + w.week_start.slice(5, 7), Minuten: w.minutes }));
  return (
    <div className="bg-[#121212] border border-white/10 p-6 col-span-2" data-testid="dashboard-week-chart">
      <p className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-6">Trainingsminuten · letzte 8 Wochen</p>
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}><XAxis dataKey="name" tick={{ fill: "#71717A", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "#71717A", fontSize: 11 }} axisLine={false} tickLine={false} width={30} allowDecimals={false} />
            <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
            <Bar dataKey="Minuten" fill="#c1ff72" radius={[2, 2, 0, 0]} /></BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Recent({ recent }) {
  return (
    <div className="bg-[#121212] border border-white/10 p-6 col-span-2" data-testid="dashboard-recent">
      <p className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-5">Zuletzt absolviert</p>
      {recent.length ? (
        <ul className="divide-y divide-white/5">{recent.slice(0, 5).map((r) => (
          <li key={r.completion_id} className="py-3 flex justify-between text-sm"><span className="text-white">{r.exercise_title}</span><span className="text-[#71717A]">{r.duration_min} Min · {r.date.split("-").reverse().join(".")}</span></li>
        ))}</ul>
      ) : <p className="text-sm text-[#71717A]">Noch keine Einheit eingetragen. Öffne eine Übung und tippe auf „Als erledigt markieren“.</p>}
    </div>
  );
}

export default function Dashboard() {
  const { member } = useMember();
  const [stats, setStats] = useState(null);
  const [exs, setExs] = useState([]);
  useEffect(() => {
    api("/me/stats").then(setStats).catch(() => setStats({ weeks: [], recent: [], this_week: {} }));
    api(`/exercises?level=${member.level}`).then((d) => setExs(d.items)).catch(() => {});
  }, [member.level]);
  const recommended = useMemo(() => {
    const eq = new Set([...(member.equipment || []), "keins"]);
    return exs.filter((e) => !e.locked && e.equipment.every((x) => eq.has(x))).slice(0, 4);
  }, [exs, member.equipment]);
  if (!stats) return <PageLoader />;
  const goalDays = (member.training_days || []).length;
  return (
    <div data-testid="dashboard-page">
      <Overline className="mb-3">Dein Training</Overline>
      <H1 className="mb-10 !text-5xl sm:!text-6xl">Hallo {member.first_name}.</H1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <TodayCard />
        <MotivationCard />
        <Stat icon={Flame} label="Tage in Folge" value={stats.streak_days ?? 0} testId="stat-streak" />
        <Stat icon={Timer} label="Minuten diese Woche" value={stats.this_week?.minutes ?? 0} testId="stat-week-minutes" />
        <Stat icon={Target} label="Trainingstage diese Woche" value={`${stats.this_week?.days ?? 0}/${goalDays}`} sub="Ziel laut Profil" testId="stat-week-goal" />
        <Stat icon={Trophy} label="Einheiten gesamt" value={stats.total_sessions ?? 0} testId="stat-total" />
        <WeekChart weeks={stats.weeks} />
        <Recent recent={stats.recent} />
        <Achievements />
      </div>
      <div className="flex items-end justify-between mt-16 mb-6">
        <div><Overline className="mb-2">Passend zu deinem Profil</Overline><p className="font-['Bebas_Neue'] text-4xl text-white">Für dich empfohlen</p></div>
        <Link to="/training/bibliothek" className="text-sm text-[#c1ff72] flex items-center gap-1" data-testid="dashboard-to-library">Alle Übungen<ArrowRight className="w-4 h-4" /></Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-recommended">{recommended.map((e) => <ExerciseCard key={e.exercise_id} ex={e} />)}</div>
    </div>
  );
}
